import { useAtomValue } from 'jotai';
import { toInteger } from 'lodash';
import { curry, filter, find, flatten, keyBy, map, size } from 'lodash/fp';
import { DateTime } from 'luxon';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import useSWRInfinite from 'swr/infinite';

import {
    GroupedPointingType,
    Leave,
    Mad,
    OdooCompany,
    OdooContract,
    OdooDomain,
    OdooEmployee,
    OdooEmployeeLeave,
    OdooLeave,
    OdooLeaveType,
    OdooMad,
    OdooPointing,
    OdooProvisionalCalendar,
    OdooPublicHoliday,
    OdooResourceCalendarAttendance,
    OdooUser,
    OdooWorkPlace,
    Pointing,
    ProvisionalCalendar,
    leaveFields,
    pointingFields,
} from '../types/models';
import { AppConfig, defaultCatchOdooError, usersAtom } from '../utils/Global';
import { dateFromOdoo, nowToOdooDatetime } from '../utils/date';
import odoo, {
    fetcherController,
    fetcherReadGroup,
    fetcherSearchRead,
    fetcherSearchReadOne,
    fetcherSearchReadWithAccess,
    standardizedOdooLeaves,
    standardizedOdooPointing,
    standardizedOdooPointings,
    unstandardizedOdooPointing,
} from '../utils/odoo';
import { PointingState, getMetaData } from '../utils/odooUtils';

const defaultConfigSwr = {
    suspense: true,
    dedupingInterval: 120000,
};

export const adjustMadsPropertiesByTime = (madsToMutate: Mad[], date: DateTime) => {
    return map((mad: Mad) => {
        const mutatedMad = {
            ...mad,
            disabled: false,
        };

        if (mad.date_end && date > mad.date_end) {
            mutatedMad.display_name = `(Terminé) ${mutatedMad.display_name}`;
            mutatedMad.disabled = true;
        } else if (mad.date_start && date < mad.date_start) {
            mutatedMad.display_name = `(Non commencé) ${mutatedMad.display_name}`;
            mutatedMad.disabled = true;
        }

        return mutatedMad;
    }, madsToMutate);
};

export const useMads = (date?: string) => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data } = useSWR<OdooMad[]>(
        mainUser
            ? [
                  'hr.madper.contract.mad',
                  { fields: ['id', 'name', 'employee_id', 'date_start', 'date_end', 'partner_id'] },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    let mads: Mad[] = map(
        (mad) => ({
            ...mad,
            display_name: mad?.partner_id?.[1] || mad.name,
            date_start: DateTime.fromSQL(mad.date_start),
            date_end: DateTime.fromSQL(mad.date_end).endOf('day'),
        }),
        data,
    );

    let madsPlaceholder: Mad[] = [
        ...mads,
        {
            id: 0,
            name: 'Autres',
            employee_id: [0, ''],
            date_start: null,
            date_end: null,
            partner_id: [0, ''],
            display_name: 'Autres',
        },
    ];

    return {
        data: mads,
        dataWithPlacehoder: madsPlaceholder,
        ...returnUtils(madsPlaceholder),
    };
};

export const useWorkPlaces = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [] } = useSWR<OdooWorkPlace[]>(
        mainUser ? ['work_place', { fields: ['id', 'name', 'city', 'mad_ids'] }, mainUser.uid] : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    let dataWithPlacehoder: OdooWorkPlace[] = [...data, { id: 0, name: 'Autres', city: '', mad_ids: [] }];

    return {
        data,
        dataWithPlacehoder,
        ...returnUtils(dataWithPlacehoder),
    };
};

export const useCompanies = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [] } = useSWR<OdooCompany[]>(
        mainUser
            ? [
                  'res.company',
                  {
                      fields: [
                          'id',
                          'name',
                          'phone',
                          'email',
                          'pointing_rubric_absence_hours_id',
                          'pointing_rubric_working_hours_id',
                          'pointing_rubric_working_night_hours_ids',
                          'pointing_rubric_travel_ids',
                          'pointing_rubric_lunch_id',
                          'pointing_rubric_lunch_night_id',
                          'pointing_rubric_bad_weather_hours_id',
                      ],
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    return {
        data,
        ...returnUtils(data),
    };
};

export const useEmployees = (domain?: OdooDomain) => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [] } = useSWR<OdooEmployee[]>(
        mainUser
            ? [
                  'hr.madper.employee',
                  {
                      fields: ['id', 'name', 'company_id', 'user_id', 'image_128', 'best_phone', 'display_name'],
                      domain,
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    const dataWithDisplayName = map((employee: OdooEmployee) => {
        // TODO: Enhance the system to accurately identify employees sharing the same name and birthdate, ensuring comprehensive inclusion of all users.
        const employeesWithSameUser = filter((e) => e.user_id[0] === employee.user_id[0], data);
        return {
            ...employee,
            display_name:
                size(employeesWithSameUser) > 1
                    ? `${employee.company_id[1]} - ${employee.display_name}`
                    : employee.display_name,
        };
    }, data);

    return {
        data,
        dataWithDisplayName,
        ...returnUtils(data),
    };
};
export const useLeavesInfinite = (
    limit: number = 10,
    domain?: OdooDomain,
    sort: string = 'state, date_start, date_end, id desc',
) => {
    const { mainUser } = useAtomValue(usersAtom);

    const getKey = (pageIndex: number, previousPageData: any) => {
        if (previousPageData && !previousPageData.length) return null; // atteint la fin
        return [
            'hr.madper.app.leave',
            {
                fields: leaveFields,
                domain: domain,
                sort: sort,
                offset: limit * pageIndex,
                limit,
            },
            mainUser?.uid,
        ]; // clé SWR
    };
    const { data, isLoading, size, setSize } = useSWRInfinite<OdooLeave[]>(getKey, fetcherSearchRead);

    const flattenData = flatten(data);

    let leaveCards: OdooLeave[] = map(
        (line) => ({
            ...line,
        }),
        flattenData,
    );

    return {
        data,
        standardizedOdooLeaves: standardizedOdooLeaves(leaveCards),
        size,
        setSize,
        ...returnUtils(leaveCards),
    };
};

export const useLeaves = (domain?: OdooDomain, sort: string = 'state, date_start, date_end, id desc') => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [], mutate } = useSWR<OdooLeave[]>(
        mainUser
            ? [
                  'hr.madper.app.leave',
                  {
                      fields: leaveFields,
                      domain,
                      sort,
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    return {
        data,
        standardizedOdooLeaves: standardizedOdooLeaves(data),
        mutate,
        ...returnUtils(data),
    };
};

export const useResourceCalendarLines = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [] } = useSWR<OdooResourceCalendarAttendance[]>(
        mainUser
            ? [
                  'resource.calendar.attendance',
                  { fields: ['id', 'calendar_id', 'dayofweek', 'week_type'], domain: [['calendar_id', '!=', false]] },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    let resourceCalendarLines = map(
        (line) => ({ ...line, calendar_id: line.calendar_id[0], dayofweek: toInteger(line.dayofweek) }),
        data,
    );

    return {
        data: resourceCalendarLines,
        ...returnUtils(resourceCalendarLines),
    };
};

export const usePublicHolidays = (date?: DateTime) => {
    const { mainUser } = useAtomValue(usersAtom);

    let domain: OdooDomain = [];

    if (date) {
        domain = [
            ['date', '>=', date.minus({ month: 1 }).startOf('month').toFormat('yyyy-MM-dd')],
            ['date', '<=', date.plus({ month: 1 }).endOf('month').toFormat('yyyy-MM-dd')],
        ];
    }

    const { data = [] } = useSWR<OdooPublicHoliday[]>(
        mainUser
            ? [
                  'hr.madper.save.public.holiday',
                  {
                      fields: ['id', 'date', 'name', 'zone', 'year'],
                      domain,
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    let publicHolidays = map(
        (line) => ({
            ...line,
            date: dateFromOdoo(line.date),
        }),
        data,
    );

    return {
        data: publicHolidays,
    };
};

export const useAppConfig = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data } = useSWR(mainUser ? `appConfig-${mainUser.uid}` : null, odoo.getAppConfig, {
        ...defaultConfigSwr,
    });

    return {
        data: { roundingHours: 15, ...data } as AppConfig,
    };
};

export const useProvisionalCalendarLines = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const {
        data = [],
        isLoading,
        mutate,
    } = useSWR<OdooProvisionalCalendar[]>(
        mainUser
            ? [
                  'hr.madper.app.pointing.provisional.calendar',
                  {
                      fields: [
                          'id',
                          'employee_id',
                          'mad_id',
                          'workplace_id',
                          'date_start',
                          'date_end',
                          'note',
                          'link_uuid',
                      ],
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        {},
    );

    return {
        data,
        isLoading,
        ...returnUtils(data),
        mutate,
    };
};

export const useInfiniteProvisionalCalendarLines = (
    limit: number = 20,
    order: string = 'date_start asc',
    from_date: DateTime = DateTime.now().startOf('day'),
) => {
    const { mainUser } = useAtomValue(usersAtom);

    const getKey = (pageIndex: number, previousPageData: any) => {
        if (previousPageData && !previousPageData.length) return null;

        return [
            'hr.madper.app.pointing.provisional.calendar',
            {
                fields: ['id', 'employee_id', 'mad_id', 'workplace_id', 'date_start', 'date_end', 'note', 'link_uuid'],
                domain: [['date_start', '>=', from_date.toISO()]],
                sort: order,
                offset: limit * pageIndex,
                limit,
            },
            mainUser?.uid,
        ];
    };

    const { data, isLoading, size, setSize } = useSWRInfinite<OdooProvisionalCalendar[]>(getKey, fetcherSearchRead);

    // useSWRInfinite data return nested array representing pages, flatten that for ease of processing
    const flattenData = flatten(data);

    let provisionalCalendars: ProvisionalCalendar[] = map(
        (line) => ({
            ...line,
            date_start: dateFromOdoo(line.date_start),
            date_end: dateFromOdoo(line.date_end),
        }),
        flattenData,
    );

    return {
        data: provisionalCalendars,
        isLoading,
        size,
        setSize,
        ...returnUtils(provisionalCalendars),
    };
};

export const useContracts = (employeeIds: number[]) => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [] } = useSWR<OdooContract[]>(
        mainUser
            ? [
                  'hr.madper.contract',
                  {
                      fields: ['id', 'name', 'date_start', 'resource_calendar_id'],
                      domain: [['employee_id', 'in', employeeIds]],
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    return {
        data,
        ...returnUtils(data),
    };
};

export const useUser = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data } = useSWR<OdooUser | null>(
        mainUser
            ? ['res.users', { fields: ['id', 'name', 'login'], domain: [['id', '=', mainUser.uid]] }, mainUser.uid]
            : null,
        fetcherSearchReadOne,
        defaultConfigSwr,
    );

    return {
        user: data ?? null,
    };
};

export const usePointing = (
    employeeIds: number[],
    domain: OdooDomain = [],
    fields?: keyof OdooPointing,
    sort: string = 'create_date desc',
) => {
    const { mainUser } = useAtomValue(usersAtom);
    const {
        data = [],
        isLoading,
        mutate,
    } = useSWR<OdooPointing[]>(
        mainUser
            ? [
                  'hr.madper.app.pointing.time',
                  {
                      fields: fields || pointingFields,
                      domain: [['employee_id', 'in', employeeIds], ...domain],
                      sort,
                  },
                  mainUser.uid,
              ]
            : null,

        fetcherSearchReadWithAccess,
        {},
    );

    const setPointing = async (
        pointingId: number,
        fields: Partial<OdooPointing>,
        context?: { bypassValidation?: boolean },
    ) => {
        await odoo.write('hr.madper.app.pointing.time', [pointingId], fields, { context });
        mutate();
    };

    return {
        data,
        standardized: standardizedOdooPointings(data),
        setPointing,
        ...returnUtils(data),
        isLoading,
        mutate,
    };
};

export const useGroupedPointing = (employeeIds: number[], disabled = false, date?: DateTime) => {
    const { mainUser } = useAtomValue(usersAtom);
    const defaultDomain = date
        ? [
              ['date', '>=', date.startOf('week').toISODate()],
              ['date', '<=', date.endOf('week').toISODate()],
          ]
        : [];

    const { data = [], mutate } = useSWR<Array<GroupedPointingType>>(
        mainUser && !disabled
            ? [
                  'hr.madper.app.pointing.time',
                  {
                      fields: ['date'],
                      domain: [
                          ...defaultDomain,
                          ['employee_id', 'in', employeeIds],
                          ['origin_id', '=', false],
                          [
                              'state',
                              'in',
                              [
                                  PointingState.WaitValidation,
                                  PointingState.AdherentValidation,
                                  PointingState.GeiqValidation,
                              ],
                          ],
                      ],
                      groupBy: ['date:day'],
                      context: { force_iso_date_read_group: true },
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherReadGroup,
        {},
    );

    return {
        data,
        mutate,
    };
};

const returnUtils = <T extends { id: number }[]>(data: T) => ({
    get ids() {
        return map('id', data);
    },
    get normalize() {
        return keyBy('id', data) as Record<number, T[0]>;
    },
    map: map<T[0]>(curry.placeholder, data),
    find: find<T[0]>(curry.placeholder, data),
});

export const useGetLimitDatePointing = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data } = useSWR<{ data: { result: { limit_date_pointing: string } } } | null>(
        mainUser ? ['/get_limit_date_pointing', mainUser.uid] : null,
        fetcherController,
        defaultConfigSwr,
    );

    const limitDatePointing = data?.data?.result?.limit_date_pointing;

    if (!limitDatePointing) {
        return DateTime.now().minus({ days: 7 }).startOf('day');
    }

    return DateTime.fromISO(limitDatePointing).startOf('day');
};

export const usePermanentEmployees = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [] } = useSWR<OdooEmployee[]>(
        mainUser
            ? ['hr.madper.employee', { fields: ['id', 'name'], domain: [['service', '=', 'permanent']] }, mainUser.uid]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    return {
        data,
    };
};

export const useLeaveTypes = () => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [] } = useSWR<OdooLeaveType[]>(
        mainUser
            ? [
                  'hr.leave.type',
                  {
                      fields: ['id', 'name', 'calendar_color', 'calendar_abbreviation'],
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    return {
        data,
    };
};

export const useEmployeeLeaves = (date: DateTime) => {
    const { mainUser } = useAtomValue(usersAtom);

    const { data = [] } = useSWR<OdooEmployeeLeave[]>(
        mainUser
            ? [
                  'hr.leave',
                  {
                      fields: [
                          'id',
                          'employee_id',
                          'holiday_status_id',
                          'request_date_from',
                          'request_date_to',
                          'request_unit_half',
                          'request_date_from_period',
                      ],
                      domain: [['state', '=', 'validate']],
                  },
                  mainUser.uid,
              ]
            : null,
        fetcherSearchRead,
        defaultConfigSwr,
    );

    return {
        data,
    };
};
